import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';

import {
  bulkCriteriaChannelsQuery,
  bulkHierarchyChannelsQuery,
} from 'graphql-queries';
import { PageSizeType } from 'design-system-web';
import { Channel } from 'lane-shared/types/ChannelType';

import { ChannelModuleSettings } from '../registry/bulkModuleRegistry';

export type SelectionMode = 'criteria' | 'parent';

type CriteriaState = {
  keyword: string;
  channelTypes: string[];
  page: number;
  pageSize: PageSizeType;
};

const INITIAL_CRITERIA: CriteriaState = {
  keyword: '',
  channelTypes: [],
  page: 0,
  pageSize: 25,
};

const HIERARCHY_PAGE_SIZE = 500;

export type ChannelListItem = Pick<Channel, '_id' | 'name' | 'slug' | 'type'> & {
  parent?: { _id: string; name?: string | null } | null;
  settings?: ChannelModuleSettings | null;
};

/**
 * Manages channel selection state for the bulk module tool.
 *
 * Mode A (criteria): paginates the admin-only `bulkModuleChannels` query with
 * keyword/type filters. The server always orders by channel name and id for
 * consistent pagination; the user manually selects rows.
 *
 * Mode B (parent): loads all descendant channels under a chosen parent via
 * `bulkModuleChannels` and auto-selects them all.
 *
 * Both queries include module settings fields so that preview counts can be
 * derived synchronously from already-loaded data — no separate fetch needed.
 */
export function useBulkChannelSelection() {
  const [mode, setMode] = useState<SelectionMode>('criteria');
  const [selectedChannels, setSelectedChannels] = useState<ChannelListItem[]>(
    []
  );
  const [criteria, setCriteria] = useState<CriteriaState>(INITIAL_CRITERIA);
  const [parentChannelId, setParentChannelId] = useState<string | null>(null);

  const selectedChannelIds = useMemo(
    () => selectedChannels.map(ch => ch._id),
    [selectedChannels]
  );

  // ── Mode A: criteria query ──────────────────────────────────────────────

  const criteriaQuery = useQuery(bulkCriteriaChannelsQuery, {
    skip: mode !== 'criteria',
    fetchPolicy: 'cache-and-network',
    variables: {
      pagination: {
        start: criteria.page * criteria.pageSize,
        perPage: criteria.pageSize,
      },
      search: {
        name: criteria.keyword
          ? { type: 'like', value: criteria.keyword }
          : undefined,
        type:
          criteria.channelTypes.length > 0
            ? { any: criteria.channelTypes }
            : undefined,
      },
    },
  });

  const criteriaChannels: ChannelListItem[] = useMemo(() => {
    return criteriaQuery.data?.bulkModuleChannels?.items ?? [];
  }, [criteriaQuery.data?.bulkModuleChannels?.items]);

  /** Snapshot only updated when criteria query finishes for the active page — avoids merging against stale rows while pagination variables have changed. */
  const criteriaChannelsAlignedRef = useRef<{
    page: number;
    pageSize: number;
    channels: ChannelListItem[];
  } | null>(null);

  useEffect(() => {
    if (mode !== 'criteria') {
      return;
    }
    if (criteriaQuery.loading) {
      return;
    }
    const vars = criteriaQuery.variables;
    const pagination =
      vars && typeof vars === 'object' && 'pagination' in vars
        ? vars.pagination
        : undefined;
    const start =
      pagination &&
      typeof pagination === 'object' &&
      'start' in pagination &&
      typeof pagination.start === 'number'
        ? pagination.start
        : undefined;
    const perPage =
      pagination &&
      typeof pagination === 'object' &&
      'perPage' in pagination &&
      typeof pagination.perPage === 'number'
        ? pagination.perPage
        : undefined;
    const expectedStart = criteria.page * criteria.pageSize;
    if (
      start !== expectedStart ||
      perPage !== criteria.pageSize ||
      start === undefined ||
      perPage === undefined
    ) {
      return;
    }
    criteriaChannelsAlignedRef.current = {
      page: criteria.page,
      pageSize: criteria.pageSize,
      channels: criteriaChannels,
    };
  }, [
    mode,
    criteriaQuery.loading,
    criteriaQuery.variables,
    criteria.page,
    criteria.pageSize,
    criteriaChannels,
  ]);

  const criteriaTotal: number =
    criteriaQuery.data?.bulkModuleChannels?.pageInfo?.total ?? 0;

  // ── Mode B: parent hierarchy query ─────────────────────────────────────

  const parentQuery = useQuery(bulkHierarchyChannelsQuery, {
    skip: mode !== 'parent' || !parentChannelId,
    fetchPolicy: 'cache-and-network',
    variables: {
      pagination: { start: 0, perPage: HIERARCHY_PAGE_SIZE },
      search: {
        includeChannelsFromHierarchy: true,
        isSub: false,
        parent: { _id: parentChannelId },
      },
    },
  });

  const parentDescendants: ChannelListItem[] = useMemo(() => {
    return parentQuery.data?.bulkModuleChannels?.items ?? [];
  }, [parentQuery.data?.bulkModuleChannels?.items]);
  const parentTotal: number =
    parentQuery.data?.bulkModuleChannels?.pageInfo?.total ?? 0;
  const parentLimit = HIERARCHY_PAGE_SIZE;
  const parentHasMoreDescendants = parentTotal > parentDescendants.length;

  // ── Selection helpers ──────────────────────────────────────────────────

  const setSelectedFromIds = useCallback(
    (ids: string[]) => {
      const aligned = criteriaChannelsAlignedRef.current;
      const visibleChannels =
        mode === 'criteria' &&
        aligned &&
        aligned.page === criteria.page &&
        aligned.pageSize === criteria.pageSize
          ? aligned.channels
          : [];

      const currentPageIds = new Set(visibleChannels.map(ch => ch._id));
      const idSet = new Set(ids);

      setSelectedChannels(prev => {
        const offPageSelections = prev.filter(ch => !currentPageIds.has(ch._id));
        const onPageSelections = visibleChannels.filter(ch =>
          idSet.has(ch._id)
        );

        return [...offPageSelections, ...onPageSelections];
      });
    },
    [criteria.page, criteria.pageSize, mode]
  );

  const clearSelection = useCallback(() => {
    setSelectedChannels([]);
  }, []);

  const handleModeChange = useCallback(
    (nextMode: SelectionMode) => {
      setMode(nextMode);
      clearSelection();
      setParentChannelId(null);
    },
    [clearSelection]
  );

  const handleParentSelected = useCallback(
    (channel: { _id: string } | null) => {
      setParentChannelId(channel?._id ?? null);
      clearSelection();
    },
    [clearSelection]
  );

  // Auto-select all descendants when parent query returns results
  const autoSelectDescendants = useCallback(() => {
    setSelectedChannels(parentDescendants);
  }, [parentDescendants]);

  const handleCriteriaChange = useCallback(
    (update: Partial<Omit<CriteriaState, 'page' | 'pageSize'>>) => {
      setCriteria(prev => ({ ...prev, ...update, page: 0 }));
      clearSelection();
    },
    [clearSelection]
  );

  const handlePageChange = useCallback((pageIndex: number) => {
    setCriteria(prev => ({ ...prev, page: pageIndex }));
  }, []);

  const handlePageSizeChange = useCallback(
    (pageSize: PageSizeType) => {
      setCriteria(prev => ({ ...prev, pageSize, page: 0 }));
      clearSelection();
    },
    [clearSelection]
  );

  const refetchChannels = useCallback(() => {
    if (mode === 'parent') {
      parentQuery.refetch();
    } else {
      criteriaQuery.refetch();
    }
  }, [mode, criteriaQuery.refetch, parentQuery.refetch]);

  return {
    mode,
    selectedChannelIds,
    selectedChannels,
    // Mode A
    criteria,
    criteriaChannels,
    criteriaTotal,
    criteriaLoading: criteriaQuery.loading,
    // Mode B
    parentChannelId,
    parentDescendants,
    parentTotal,
    parentLimit,
    parentHasMoreDescendants,
    parentLoading: parentQuery.loading,
    // Actions
    handleModeChange,
    handleCriteriaChange,
    handlePageChange,
    handlePageSizeChange,
    handleParentSelected,
    autoSelectDescendants,
    setSelectedFromIds,
    clearSelection,
    refetchChannels,
  };
}
